// =============================================================================
// Tầng lưu chứng từ Hạ COD trên Google Drive (qua backend /cod-reduction-docs). Tái dùng
// hạ tầng doc của Duyệt chi (validate + tên file + cấu trúc thư mục, nhánh 'Ha COD').
// Backend hỏi DB `cod_reduction_can_attach` TRƯỚC khi đẩy file — cùng luật với policy
// INSERT của cod_reduction_attachments.
// =============================================================================
import { backendGet, backendRequest, backendUploadForm } from '@/lib/backend'
import {
  EXPENSE_DOC_ALLOWED_MIME,
  EXPENSE_DOC_MAX_BYTES,
  buildDocFileName,
  buildExpenseDocSubfolder,
} from '@/features/expenses/expense-doc-files'
import type { CodReductionAttachmentKind } from '@/types/db'
import { ATTACHMENT_FILE_LABEL } from './cod-reduction-utils'

export { EXPENSE_DOC_ACCEPT as COD_REDUCTION_DOC_ACCEPT } from '@/features/expenses/expense-doc-files'

export type CodReductionUploadMeta = {
  requestId: number
  code: string // mã chứng từ (HC-12-1 / HC-12-XN-1)
  kind: CodReductionAttachmentKind
  createdAt: string | null // ISO tạo đề nghị → thư mục <YYYY-MM>/Ha COD/<DD>
  employee: string // tên người đề nghị (cấp thư mục)
}

export async function uploadCodReductionDoc(
  file: File,
  meta: CodReductionUploadMeta,
): Promise<{ path: string; name: string; mime: string }> {
  if (!EXPENSE_DOC_ALLOWED_MIME.has(file.type)) {
    throw new Error('Định dạng không hỗ trợ. Dùng ảnh (JPG/PNG), PDF hoặc XML.')
  }
  if (file.size > EXPENSE_DOC_MAX_BYTES) throw new Error('File quá lớn (tối đa 15 MB).')
  const fileName = buildDocFileName(meta.code, ATTACHMENT_FILE_LABEL[meta.kind], file.name || 'file')
  const fd = new FormData()
  fd.append('file', file)
  fd.append('fileName', fileName)
  fd.append('subfolder', buildExpenseDocSubfolder(meta.createdAt, meta.employee, 'Ha COD'))
  fd.append('requestId', String(meta.requestId))
  fd.append('kind', meta.kind)
  const res = await backendUploadForm('cod-reduction-docs', fd)
  if (!res.ok) {
    const j = (await res.json().catch(() => ({}))) as Record<string, string>
    throw new Error(j.hint || j.message || j.error || 'Tải file lên Drive thất bại')
  }
  const j = (await res.json()) as { fileId: string; name: string; mimeType: string }
  return { path: j.fileId, name: j.name || fileName, mime: j.mimeType }
}

// URL xem file: tải blob qua backend (RLS kiểm quyền xem) → object URL (caller revoke).
export async function codReductionDocViewUrl(att: { file_path: string }): Promise<string> {
  const res = await backendGet(`cod-reduction-docs/${encodeURIComponent(att.file_path)}`)
  if (!res.ok) throw new Error('Không tải được file từ Drive')
  return URL.createObjectURL(await res.blob())
}

export async function downloadCodReductionDoc(att: { file_path: string; file_name: string }): Promise<void> {
  const href = await codReductionDocViewUrl(att)
  const a = document.createElement('a')
  a.href = href
  a.download = att.file_name
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(href), 10_000)
}

// Gỡ chứng từ: backend xoá ROW qua RLS (đúng người đính + còn gỡ được) rồi mới xoá file Drive.
export async function deleteCodReductionDoc(filePath: string): Promise<void> {
  const { error } = await backendRequest('DELETE', `cod-reduction-docs/${encodeURIComponent(filePath)}`)
  if (error) throw new Error(error.hint || error.error || 'Gỡ chứng từ thất bại')
}
